import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setshowPermissionDialog } from '../../store/mediaPlaybackSlice';
import { setOpenModal } from '../../../ControlPanel/store/controlPanelSlice';
/**
 * AudioPlayback component renders remote audio tracks as <audio> elements.
 * It uses the mediaControls slice as the source of truth for speaker device and active state.
 * If the user has not interacted with the document, it will retry playback on interaction.
 * @param {Object} props
 * @param {Array} props.remoteAudioTracks - Array of remote audio TrackPublication objects.
 */
const AudioPlayback = ({ remoteAudioTracks }) => {
  const { speakerActive, selectedSpeaker } = useSelector(state => state.mediaControls);
  const audioRefs = useRef({});
  const intervalRef = useRef(null);
  const dispatch = useDispatch();

  // Attach tracks to audio elements
  useEffect(() => {
    remoteAudioTracks.forEach(publication => {
      const audioEl = audioRefs.current[publication.trackSid];
      if (audioEl && publication.track) {
        publication.track.attach(audioEl);
      }
    });
    // Detach on cleanup
    return () => {
      remoteAudioTracks.forEach(publication => {
        const audioEl = audioRefs.current[publication.trackSid];
        if (audioEl && publication.track) {
          publication.track.detach(audioEl);
        }
      });
    };
  }, [remoteAudioTracks]);


  // Set sinkId (output device) and play/pause based on Redux state
  useEffect(() => {
    remoteAudioTracks.forEach(publication => {
      const audioEl = audioRefs.current[publication.trackSid];
      if (audioEl) {
        // Set sinkId if supported and selectedSpeaker is set
        if (selectedSpeaker && audioEl.setSinkId) {
          audioEl.setSinkId(selectedSpeaker.deviceId).catch(() => {});
        }
        // Play or pause based on speakerActive
        if (speakerActive) {
          // Try to play, if blocked, set up a retry on user interaction
          const tryPlay = () => {
            audioEl.play().catch((err) => {
              if (
                err.name === "NotAllowedError" ||
                (err.message &&
                  err.message.includes("Autoplay is only allowed"))
              ) {
               dispatch(setshowPermissionDialog(true)) ; 
               dispatch(setOpenModal("permission_audio_dialog"));
               
                }
              // If play fails, set up interval to retry on user interaction
              if (!intervalRef.current) {
                intervalRef.current = setInterval(() => {
                  audioEl.play().then(() => {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                  }).catch(() => { });
                }, 1000);
              }
            });
          };
          tryPlay();
        } else {
          audioEl.pause();
        }
      }
    });
    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [remoteAudioTracks, speakerActive, selectedSpeaker]);



  return (
    <>
      {remoteAudioTracks.map((publication) => (
        <audio
          key={publication.trackSid}
          ref={el => {
            if (el) audioRefs.current[publication.trackSid] = el;
          }}
          autoPlay
          playsInline
          muted={!speakerActive} 
          controls={false}
          style={{ display: 'none' }}
        />
      ))}
    </>
  );
};

export default AudioPlayback;
